import { useMemo } from "react";
import uniq from "lodash/uniq";
import { UICart, UIFlightData, UIUnifiedPriceBreakdownItem } from "@flights/types";
import { useI18n } from "@bookingcom/lingojs-react";
import usePointOfSale from "../../../hooks/usePointOfSale";
import useRouteName from "../../../hooks/useRouteName";
import { useSelector } from "react-redux";
import { getAncillaries } from "../../../store/flightDetails/selectors";
import {
  getExtraProducts,
  getPreOptedAncillaries,
  getSelectedExtraProducts
} from "../../../ancillaries/store/extraProducts/selectors";
import { priceBreakdownUtils, pricingUtils, localizationUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { selectedAncillariesToAddedAncillaries } from "../../../store/selectors/getAddedAncillariesExtraProduct";
import { type MpProduct } from "@bookingcom/mp-flights";
import { transformDisclaimersForExtras, transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { transfromMultiProductsToOrderExtras } from "./transforms/multiProducts";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useEvents from "../../../hooks/useEvents";
import isEmpty from "lodash/isEmpty";
import { getAttachedAncillaries } from "./helpers";

export type UnifiedPriceBreakdownCheckoutControllerProps = {
  cart?: UICart;
  flight?: UIFlightData;
  multiProducts?: MpProduct[];
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownCheckoutController(props: UnifiedPriceBreakdownCheckoutControllerProps) {
  const { cart, flight, multiProducts } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const ancillaries = useSelector(getAncillaries);
  const extraProducts = useSelector(getExtraProducts);
  const preOptedAncillaries = useSelector(getPreOptedAncillaries);
  const selectedExtraProducts = useSelector(getSelectedExtraProducts);
  const segments = useMemo(() => cart?.offer.segments || flight?.segments, [cart, flight]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || []
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );

  const unifiedPriceBreakdown = useMemo(() => {
    const upb = cart?.unifiedPriceBreakdown || flight?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownCheckoutController - product id is missing - route "${routeName}"`
      );
    }

    return upb;
  }, [cart, flight, routeName, squeak]);

  const allAncillaries = useMemo(() => {
    return !isEmpty(extraProducts) ? extraProducts : !isEmpty(ancillaries) ? ancillaries : flight?.ancillaries || {};
  }, [flight, ancillaries, extraProducts]);

  const preOptedFlexibleTicket = useMemo(
    () => preOptedAncillaries.flexibleTicket,
    [preOptedAncillaries.flexibleTicket]
  );

  const flexibleTicketAncillary = useMemo(() => {
    return allAncillaries.flexibleTicket;
  }, [allAncillaries.flexibleTicket]);

  const extras = useMemo(() => {
    const data = selectedAncillariesToAddedAncillaries(selectedExtraProducts, allAncillaries) || {};
    // If the user has selected a flexible ticket, we need to add it to the list of selected extras.
    if (
      preOptedFlexibleTicket &&
      !data.flexibleTicket &&
      flexibleTicketAncillary &&
      ["checkout-pax", "checkout-extras", "checkout-ticket-type"].includes(routeName)
    ) {
      const len = flexibleTicketAncillary.travellers.length;
      const price = pricingUtils.sumPriceBreakdown(new Array(len).fill(flexibleTicketAncillary.priceBreakdown));
      data.flexibleTicket = {
        price,
        reference: flexibleTicketAncillary.airProductReference,
        travelerPrices: []
      };
    }
    const ancillariesFromMultiProducts = transfromMultiProductsToOrderExtras(multiProducts);
    return { ...data, ...ancillariesFromMultiProducts };
  }, [
    selectedExtraProducts,
    allAncillaries,
    preOptedFlexibleTicket,
    flexibleTicketAncillary,
    routeName,
    multiProducts
  ]);

  const addedItems = useMemo(() => {
    const allItems = priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, extras, baseOptions);
    // remove duplicates from already added & cart
    const allItemsFiltred = allItems.filter((item: UIUnifiedPriceBreakdownItem) => {
      return !cart?.unifiedPriceBreakdown?.items?.some((cartItem: UIUnifiedPriceBreakdownItem) => {
        return (
          cartItem.id === item.id &&
          cartItem.scope === item.scope &&
          cartItem.title === item.title &&
          cartItem.price.units === item.price.units &&
          cartItem.price.nanos === item.price.nanos
        );
      });
    });
    return allItemsFiltred;
  }, [i18n, extras, baseOptions, cart?.unifiedPriceBreakdown?.items]);

  const disclaimers = useMemo(() => {
    return uniq([
      ...transformDisclaimersForPriceBreakdown(cart?.price || flight?.priceBreakdown),
      ...transformDisclaimersForExtras(extras)
    ]);
  }, [cart, flight, extras]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  const attachedAncillaries = useMemo(
    () => getAttachedAncillaries(unifiedPriceBreakdown, addedItems),
    [unifiedPriceBreakdown, addedItems]
  );

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    addedItems,
    disclaimers,
    expandedByDefault,
    attachedAncillaries
  };
}
